import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../CSS/Industry.css';
import { IndustryData, IndustryItem } from '../Data/industries';
import Reveal from './Reveal';

const pad = (n: number) => `0${n}`.slice(-2);

const ItemGrid: React.FC<{ items: IndustryItem[] }> = ({ items }) => (
  <div className="ind-grid">
    {items.map((item, i) => (
      <div className="ind-item" key={item.title} style={{ ['--i' as string]: i } as React.CSSProperties}>
        <h3 className="ind-item-title">{item.title}</h3>
        <p className="ind-item-text">{item.text}</p>
        {item.link && (
          <Link to={item.link} className="ind-item-link">
            {`Explore ${item.title}`}
          </Link>
        )}
      </div>
    ))}
  </div>
);

const IndustryPage: React.FC<{ data: IndustryData; slug: string }> = ({ data, slug }) => {
  const [active, setActive] = useState(0);

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = data.capabilities.length - 1;
    let next = index;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else return;
    e.preventDefault();
    setActive(next);
    document.getElementById(`${slug}-tab-${next}`)?.focus();
  };

  return (
    <div className="industry-page">
      {/* Banner */}
      <div className="industry_banner" style={{ backgroundImage: `url('${data.banner}')` }}>
        <div className="industry-banner-overlay"></div>
        <div className="industry-banner-content">
          <h1 className="industry-banner-title">{data.name}</h1>
          <p className="industry-banner-subtitle">{data.subtitle}</p>
        </div>
      </div>

      {/* Intro + animated flow */}
      <div className="ind-section ind-intro">
        <Reveal className="ind-container">
          <p className="ind-intro-text">{data.intro}</p>
          {data.chips && (
            <ul className="ind-chips">
              {data.chips.map((chip) => (
                <li key={chip}>{chip}</li>
              ))}
            </ul>
          )}
          <div className="ind-flow" aria-hidden="true">
            <span className="ind-node">{data.flow[0]}</span>
            <span className="ind-line"></span>
            <span className="ind-node">{data.flow[1]}</span>
            <span className="ind-line"></span>
            <span className="ind-node">{data.flow[2]}</span>
          </div>
        </Reveal>
      </div>

      {/* Capabilities explorer */}
      <div className="ind-section ind-capabilities">
        <Reveal className="ind-container">
          <h2 className="ind-section-title">{data.capabilitiesTitle}</h2>
          <div className="ind-explorer">
            <div className="ind-tabs" role="tablist" aria-label={data.capabilitiesTitle}>
              {data.capabilities.map((cap, i) => (
                <button
                  key={cap.title}
                  id={`${slug}-tab-${i}`}
                  type="button"
                  role="tab"
                  aria-selected={i === active}
                  aria-controls={`${slug}-panel-${i}`}
                  tabIndex={i === active ? 0 : -1}
                  className={`ind-tab${i === active ? ' active' : ''}`}
                  onClick={() => setActive(i)}
                  onKeyDown={(e) => onKeyDown(e, i)}
                >
                  <span className="ind-tab-number">{pad(i + 1)}</span>
                  <span className="ind-tab-title">{cap.title}</span>
                </button>
              ))}
            </div>

            <div className="ind-panels">
              {data.capabilities.map((cap, i) => (
                <div
                  key={cap.title}
                  id={`${slug}-panel-${i}`}
                  role="tabpanel"
                  aria-labelledby={`${slug}-tab-${i}`}
                  hidden={i !== active}
                  className="ind-panel"
                >
                  {cap.image ? (
                    <img className="ind-panel-image" src={cap.image} alt={cap.imageAlt || cap.title} loading="lazy" decoding="async" />
                  ) : (
                    <div className="ind-panel-badge" aria-hidden="true">{pad(i + 1)}</div>
                  )}
                  <div className="ind-panel-body">
                    <h3 className="ind-panel-title">{cap.title}</h3>
                    <p className="ind-panel-text">{cap.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      {data.also && (
        <div className="ind-section ind-also">
          <Reveal className="ind-container">
            <h2 className="ind-section-title">{data.also.title}</h2>
            <ItemGrid items={data.also.items} />
          </Reveal>
        </div>
      )}

      {data.closing && (
        <div className="ind-section ind-closing">
          <Reveal className="ind-container">
            <h2 className="ind-section-title">{data.closing.title}</h2>
            <ItemGrid items={data.closing.items} />
          </Reveal>
        </div>
      )}

      {/* Call to action */}
      <div className="ind-cta">
        <div className="ind-cta-content">
          <h2 className="ind-cta-title">{data.cta}</h2>
          <Link to="/contact/" className="ind-btn">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default IndustryPage;
